import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { AlertTriangle, Banknote, CalendarClock } from "lucide-react";
import { getPayoutAlert } from "@/lib/payout-alert.functions";
import type { PayoutAlertInfo } from "@/lib/payout-alert.server";

const ALERT_DAYS = 3;

function currency(amount: number, code: string) {
  try {
    return amount.toLocaleString("en-US", { style: "currency", currency: code.toUpperCase() });
  } catch {
    return `${amount.toFixed(2)} ${code.toUpperCase()}`;
  }
}

function whenLabel(days: number | null) {
  if (days == null) return "date not set yet";
  if (days < 0) return "expected already";
  if (days === 0) return "arriving today";
  if (days === 1) return "arriving tomorrow";
  return `arriving in ${days} days`;
}

/**
 * Next payout at a glance, with a warning band once it is within a few days so
 * staff see it without opening the payouts page.
 */
export function PayoutAlertCard() {
  const load = useServerFn(getPayoutAlert);
  const [info, setInfo] = useState<PayoutAlertInfo | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    load({})
      .then((result) => {
        if (active) setInfo(result);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [load]);

  if (failed) return null;

  const payout = info?.payout ?? null;
  const due = Boolean(payout?.due);
  const needsAttention = (info?.requirementsDue?.length ?? 0) > 0 || info?.payoutsEnabled === false;

  return (
    <section
      className={`rounded-2xl border p-5 ${
        due || needsAttention
          ? "border-[var(--color-ember)] bg-[var(--color-ember)]/10"
          : "border-border bg-card/60"
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          <Banknote size={15} aria-hidden /> Next payout
        </h2>
        <Link to="/admin/payouts" className="text-xs underline text-muted-foreground hover:text-foreground">
          Payout details
        </Link>
      </div>

      {!info ? (
        <p className="mt-3 text-sm text-muted-foreground">Checking the payment account…</p>
      ) : info.error ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Payout information isn&rsquo;t available right now.
        </p>
      ) : payout ? (
        <>
          <p className="mt-3 text-3xl font-semibold text-foreground">
            {payout.amount.toLocaleString("en-US", {
              style: "currency",
              currency: (payout.currency ?? "usd").toUpperCase(),
            })}
          </p>
          <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <CalendarClock size={14} aria-hidden />
            {whenLabel(payout.daysUntil)}
            {payout.arrival_date
              ? ` · ${new Date(payout.arrival_date).toLocaleDateString("en-US", {
                  timeZone: "UTC",
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}`
              : ""}
          </p>
          {due && (
            <p className="mt-3 rounded-md bg-background/60 px-3 py-2 text-xs font-medium text-[var(--color-ember)]">
              Payout lands within {ALERT_DAYS} days — an email reminder has gone out to staff.
            </p>
          )}
        </>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">No payout scheduled right now.</p>
      )}

      {info?.balance && (
        <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-border/60 pt-3 text-sm">
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">Coming in</dt>
            <dd className="mt-0.5 font-semibold text-foreground">
              {currency(info.balance.total, info.balance.currency)}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">Ready</dt>
            <dd className="mt-0.5 text-foreground">
              {currency(info.balance.available, info.balance.currency)}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">Still clearing</dt>
            <dd className="mt-0.5 text-foreground">
              {currency(info.balance.pending, info.balance.currency)}
            </dd>
          </div>
        </dl>
      )}

      {needsAttention && (
        <p className="mt-3 flex items-start gap-2 text-xs text-[var(--color-ember)]">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden />
          {info?.payoutsEnabled === false
            ? "Payouts are paused on the account — finish the outstanding steps on the payouts page."
            : "The payment account is asking for more information before the next payout."}
        </p>
      )}

      {info?.environment === "sandbox" && (
        <p className="mt-2 text-[11px] text-muted-foreground">Test-mode account — no real money moves yet.</p>
      )}
    </section>
  );
}
